import { cva, type VariantProps } from 'class-variance-authority';
import { motion } from 'motion/react';
import { forwardRef } from 'react';

import { cn } from '@/lib/utils';

const emptyRootVariants = cva(
  ['flex flex-col items-center justify-center', 'text-center text-balance'],
  {
    variants: {
      variant: {
        hero: [
          'gap-5 px-6 py-16',
          'rounded-xl border border-dashed border-border-strong',
        ],
        plain: ['gap-5 px-6 py-24'],
        compact: ['gap-3 px-4 py-8'],
        action: [
          'gap-4 px-6 py-16',
          'rounded-xl border border-dashed border-border-strong',
        ],
      },
      offset: {
        default: '-translate-y-10',
        sm: '-translate-y-3',
      },
    },
    defaultVariants: {
      variant: 'hero',
    },
  }
);

const Root = forwardRef<
  HTMLDivElement,
  React.ComponentProps<'div'> & VariantProps<typeof emptyRootVariants>
>(({ className, variant, offset, ...props }, ref) => (
  <div
    ref={ref}
    data-slot="empty"
    data-variant={variant ?? 'hero'}
    className={cn(emptyRootVariants({ variant, offset }), className)}
    {...props}
  />
));

Root.displayName = 'EmptyRoot';

function Header({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-header"
      className={cn(
        'flex flex-col items-center gap-1',
        'max-w-sm',
        'text-center',
        className
      )}
      {...props}
    />
  );
}

const emptyMediaVariants = cva(
  [
    'flex shrink-0 items-center justify-center',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        default: 'bg-transparent',
        icon: [
          'size-10 rounded-lg',
          'border-[0.5px] border-border-strongest',
          'bg-secondary text-foreground-muted',
          "[&_svg:not([class*='size-'])]:size-6 [&_svg]:stroke-[1.3]",
        ],
        translucentIcon: [
          'size-10 rounded-lg',
          'bg-grayA-2 text-foreground',
          "[&_svg:not([class*='size-'])]:size-6",
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

function Media({
  className,
  variant = 'default',
  ...props
}: React.ComponentProps<'div'> & VariantProps<typeof emptyMediaVariants>) {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  );
}

function Title({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-title"
      className={cn('text-lg font-medium text-foreground', className)}
      {...props}
    />
  );
}

function Description({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-description"
      className={cn(
        'text-sm/relaxed text-foreground-muted text-center text-balance',
        '[&>a]:underline [&>a]:underline-offset-4',
        '[&>a:hover]:text-primary',
        className
      )}
      {...props}
    />
  );
}

function Content({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-content"
      className={cn(
        'flex flex-col items-center gap-4',
        'w-full max-w-sm min-w-0',
        'text-sm text-balance',
        className
      )}
      {...props}
    />
  );
}

function Actions({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="empty-actions"
      className={cn('flex flex-row items-center gap-2 mt-1', className)}
      {...props}
    />
  );
}

export const Empty = {
  Root: motion.create(Root),
  Header,
  Title,
  Description,
  Content,
  Media,
  Actions,
};
